/**
 * MostEngaged — "Community Pulse" (Bento top-right quadrant)
 *
 * The post currently generating the most premium reactions or active discourse.
 * Uses the highlights feed (posts with 3+ reactions) and takes the top result.
 */

import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useHighlights } from './queries';

interface MostEngagedProps {
  clubId: string;
  theme: ClubTheme;
  onPostClick?: (post: SocialPost) => void;
}

export default function MostEngaged({ clubId, theme, onPostClick }: MostEngagedProps) {
  const { data: highlights = [], isLoading } = useHighlights(clubId);

  // Take the most engaged post (highlights are already sorted by engagement)
  const post = highlights[0] || null;

  if (isLoading) {
    return (
      <div
        className="flex h-full flex-col rounded-2xl p-5"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: theme.colors.accent }}>
          Community Pulse
        </span>
        <div className="flex-1 space-y-3">
          <div className="h-4 w-2/3 animate-pulse rounded" style={{ backgroundColor: `${theme.colors.muted}15` }} />
          <div className="h-3 w-full animate-pulse rounded" style={{ backgroundColor: `${theme.colors.muted}10` }} />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center rounded-2xl p-5 text-center"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: theme.colors.accent }}>
          Community Pulse
        </span>
        <p className="text-sm" style={{ color: theme.colors.muted }}>
          No active discourse yet. Be the first to engage.
        </p>
      </div>
    );
  }

  const authorName = post.author?.full_name || 'Unknown';
  const avatarUrl = post.author?.avatar_url;
  const bodyPreview = (post.body || '').slice(0, 150);
  const totalReactions = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);
  const topEmojis = Object.entries(post.reactions || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([emoji]) => emoji);

  return (
    <div
      className="flex h-full flex-col rounded-2xl p-5 transition-shadow hover:shadow-lg cursor-pointer"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.muted}15`,
      }}
      onClick={() => onPostClick?.(post)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onPostClick?.(post)}
    >
      {/* Header */}
      <span className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: theme.colors.accent }}>
        Community Pulse
      </span>

      {/* Author */}
      <div className="flex items-center gap-2 mb-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
            style={{ backgroundColor: theme.colors.accent, color: theme.colors.bg }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}
        <span className="text-sm font-semibold truncate" style={{ color: theme.colors.text }}>
          {authorName}
        </span>
      </div>

      {/* Body */}
      <p
        className="flex-1 text-sm leading-relaxed line-clamp-3"
        style={{ color: theme.colors.text }}
      >
        {bodyPreview}{bodyPreview.length >= 150 ? '...' : ''}
      </p>

      {/* Engagement bar */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {topEmojis.length > 0 && (
            <div className="flex -space-x-0.5">
              {topEmojis.map((emoji) => (
                <span key={emoji} className="text-sm">{emoji}</span>
              ))}
            </div>
          )}
          <span className="text-[11px] font-medium" style={{ color: theme.colors.muted }}>
            {totalReactions} reaction{totalReactions !== 1 ? 's' : ''}
          </span>
        </div>
        {post.comment_count > 0 && (
          <span className="text-[11px]" style={{ color: theme.colors.muted }}>
            {post.comment_count} comment{post.comment_count !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
